/**
 * signupSheet.ts — #1896. Everything the sign-up sheet snap has to DECIDE,
 * separated from the vision call and the writes.
 *
 * In `_shared/` because the Edge Function files the rows and the app's lead-time
 * picker recomputes the same dates. Two implementations of "three days before"
 * is how a card and its reminder end up on different days.
 */

// ── Row matching ───────────────────────────────────────────────────────────

export type SignupRowKind = "timeslot" | "bring_to_event" | "bring_by_deadline";

export interface SignupRow {
  index: number;
  item: string;
  /** The name column as printed on the sheet, e.g. "Kasandra / Carson Botkin". */
  person_text: string;
  kind: SignupRowKind;
  slot_start: string | null;
  kid_guess: string | null;
  buyable: boolean;
  store_query: string | null;
}

/** Word-boundary, case-insensitive. "Kas" must not match "Kassandra's mom". */
function containsName(haystack: string, name: string): boolean {
  const h = (haystack ?? "").toLowerCase();
  const n = (name ?? "").trim().toLowerCase();
  if (!n) return false;
  // Escape, then require a non-letter on each side.
  const esc = n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^a-z])${esc}([^a-z]|$)`, "i").test(h);
}

/**
 * ⚠️ A single-letter name is an INITIAL, and an initial matches only a row
 * whose name text IS that letter.
 *
 * Her kids are "C" and "J" on some sheets. Treating those as ordinary names
 * makes "C" match "Carson", "Chloe", "Mac", and the "c" in "picnic" — and a
 * false match here does not fail loudly, it files somebody else's row to her
 * Today tab.
 */
function matchesKidName(personText: string, kidName: string): boolean {
  const name = (kidName ?? "").trim();
  if (!name) return false;
  if (name.length === 1) {
    return (personText ?? "").trim().toLowerCase() === name.toLowerCase();
  }
  return containsName(personText, name);
}

/** Levenshtein distance, capped: returns cap+1 as soon as it is exceeded. */
function editDistance(a: string, b: string, cap = 1): number {
  if (Math.abs(a.length - b.length) > cap) return cap + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      cur.push(v);
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > cap) return cap + 1;
    prev = cur;
  }
  return prev[b.length];
}

/**
 * Her family name, close enough. Handwriting is read imperfectly: on the
 * first real sheet (Oct 27 2026) "Carson Botkin" came back "Carson Rotkin".
 * One letter off, names of 5+ letters only, so "Botkin" survives a misread
 * capital while short names ("Hall", "Kato") can't collide with each other.
 */
function containsFamilyName(personText: string, lastName: string): boolean {
  const target = (lastName ?? "").trim().toLowerCase();
  if (target.length < 5) return target.length > 0 && containsName(personText, target);
  const words = (personText ?? "").toLowerCase().split(/[^a-z]+/).filter(Boolean);
  return words.some((w) => w.length >= 5 && editDistance(w, target, 1) <= 1);
}

export interface MatchInput {
  rows: readonly Pick<SignupRow, "index" | "person_text">[];
  /** Her own first name, from auth metadata. */
  firstName: string | null;
  /** Her family name, from auth metadata when present. Fuzzy-matched. */
  lastName?: string | null;
  /** Kid names, from the same source TodayTab's kid list uses. */
  kidNames: readonly string[];
}

export interface MatchResult {
  matched_rows: number[];
  /**
   * True when Kin must ASK before filing anything.
   *
   * ⚠️ Only a match on HER OWN first name settles it. A kid's name on a sheet
   * is often another family's kid with the same name, and a sheet can carry
   * several of her kids' rows where only one is hers to bring. Kasandra's
   * decision, Oct 22: ask unless her own name is on a row.
   */
  needs_choice: boolean;
}

export function matchSignupRows(input: MatchInput): MatchResult {
  const rows = Array.isArray(input?.rows) ? input.rows : [];
  const kids = Array.isArray(input?.kidNames) ? input.kidNames : [];
  const first = input?.firstName ?? "";
  const last = input?.lastName ?? "";

  const matched: number[] = [];
  let matchedByHerName = false;

  for (const row of rows) {
    const text = row?.person_text ?? "";
    const byHer = containsName(text, first);
    const byKid = kids.some((k) => matchesKidName(text, k));
    // A family-name match is a SUGGESTION, never a reason to file without
    // asking: another family can share a surname. It lets Kin say "is this
    // yours?" about one row instead of listing every row on the sheet.
    const byFamily = !!last && containsFamilyName(text, last);
    if (byHer) matchedByHerName = true;
    if (byHer || byKid || byFamily) matched.push(row.index);
  }

  return { matched_rows: matched, needs_choice: !matchedByHerName };
}

// ── Lead dates ─────────────────────────────────────────────────────────────

export const DEFAULT_LEAD_DAYS = 3;
export const MIN_LEAD_DAYS = 1;
export const MAX_LEAD_DAYS = 14;

/**
 * When to nudge her: `dueDate` minus `leadDays`, floored at today.
 *
 * ⚠️ FLOORED, not skipped. A sheet due tomorrow with a 3-day lead would
 * otherwise schedule a reminder for two days ago, which fires immediately or
 * never depending on which runner sees it first. Today is the honest answer:
 * she still needs to know.
 *
 * Returns null for an unparseable date rather than guessing one, because every
 * caller writes it to a column.
 */
export function computeSendBy(
  dueDate: string | null | undefined,
  leadDays: number,
  today: string,
): string | null {
  const due = parseYmd(dueDate);
  const now = parseYmd(today);
  if (!due || !now) return null;
  const lead = Number.isFinite(leadDays) ? Math.round(leadDays) : DEFAULT_LEAD_DAYS;
  const target = new Date(due);
  target.setUTCDate(target.getUTCDate() - lead);
  return toYmd(target < now ? now : target);
}

function parseYmd(value: string | null | undefined): Date | null {
  if (typeof value !== "string") return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  // Date.UTC normalises impossible dates, so round-trip to reject them.
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return dt;
}

function toYmd(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

/** "Tue Oct 20" for the card body and the reply. */
export function weekdayShort(ymd: string | null | undefined): string | null {
  const d = parseYmd(ymd);
  if (!d) return null;
  const wd = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getUTCDay()];
  const mo = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getUTCMonth()];
  return `${wd} ${mo} ${d.getUTCDate()}`;
}

/** The dedupe key: one filing per (sheet, item, date). */
export function signupDedupeKey(sheetTitle: string, item: string, date: string | null): string {
  const norm = (s: string) => (s ?? "").trim().toLowerCase().replace(/\s+/g, " ");
  return `${norm(sheetTitle)}|${norm(item)}|${date ?? ""}`;
}

// ── Kin's reply ────────────────────────────────────────────────────────────

export interface FiledEvent {
  /** "Pumpkin Party" */
  sheetTitle: string;
  /** The event's own date. */
  date: string | null;
  /** "3:30", when it is a time slot. */
  time: string | null;
  /** "Carson", when a kid matched. */
  child: string | null;
}

export interface FiledCard {
  item: string;
  /** The date the nudge fires. */
  nudgeDate: string | null;
  leadDays: number;
  /** A deadline sheet says "due", an event sheet says the event is on. */
  dueDate: string | null;
}

export interface ReplyInput {
  events: readonly FiledEvent[];
  cards: readonly FiledCard[];
  isPro: boolean;
  /** Rows that were already filed by an earlier snap of the same sheet. */
  alreadyFiled: readonly string[];
}

/**
 * What Kin says, built from what was actually WRITTEN rather than by the model.
 *
 * ⚠️ TIER-AWARE, and the split is narrower than it looks. EVERYONE gets the
 * lead-date push (the 2026-07-21 ruling makes one-off scheduled tasks free),
 * so "Kin reminds you" is true for both tiers and must be said to both. Only
 * the TEXT is Pro, so only the Pro line may mention a text.
 *
 * Saying "Kin reminds you by push and text" to a free account would be the
 * house-rule-16 failure one layer up: a promise about a channel she does not
 * have.
 */
/**
 * #1998 — the free reply's closing line, exported so the app can turn exactly
 * this line into a tap through to Kin Pro. The words are unchanged.
 */
export const SIGNUP_PRO_LINE = "Want that reminder as a text too? That's Kin Pro.";

/**
 * #1998 — does this reply end on the Pro line? Returned to the app as
 * `pro_upsell` so the client never has to guess from text, and Pro accounts
 * (whose reply never carries the line) can never get the link.
 */
export function signupProUpsell(reply: string, isPro: boolean): boolean {
  return !isPro && typeof reply === "string" && reply.endsWith(SIGNUP_PRO_LINE);
}

export function buildSignupReply(input: ReplyInput): string {
  const events = input?.events ?? [];
  const cards = input?.cards ?? [];
  const already = input?.alreadyFiled ?? [];

  if (events.length === 0 && cards.length === 0 && already.length === 0) {
    return "I couldn't find a row to file on that one. Want to try again?";
  }

  const lines: string[] = ["Filed. 📋"];

  for (const ev of events) {
    const when = weekdayShort(ev.date);
    const who = ev.child ? `${ev.child}'s ` : "";
    const at = ev.time ? ` at ${ev.time}` : "";
    lines.push(`• ${who}${ev.sheetTitle} is on Today${when ? ` for ${when}` : ""}${at}.`);
  }

  for (const card of cards) {
    const nudge = weekdayShort(card.nudgeDate);
    const due = weekdayShort(card.dueDate);
    const dueBit = due ? `, due ${due}` : "";
    const remind = nudge
      ? ` Kin reminds you ${nudge}, ${card.leadDays} day${card.leadDays === 1 ? "" : "s"} ahead${input.isPro ? ", by push and text" : ""}.`
      : "";
    lines.push(`• Reminder card: ${card.item}${dueBit}.${remind} Tap the card to change when.`);
  }

  for (const item of already) {
    lines.push(`• ${item} was already filed, so Kin left it alone.`);
  }

  if (events.length > 0 && cards.length === 0) {
    lines.push("Nothing to bring.");
  }

  lines.push(
    input.isPro
      ? "It's all in Tasks if you want to peek."
      : SIGNUP_PRO_LINE,
  );

  return lines.join("\n");
}

/** The question Kin asks when more than one row could be hers. */
export const SIGNUP_CHOICE_PROMPT = "I see a few names on this one. Which row is yours?";

/** When Kin found a likely row: one question, one tap. */
export const SIGNUP_CONFIRM_PROMPT = "I think I found yours. Is this it?";

/** Shown the instant she taps, so a vision-free filing never looks frozen. */
export const SIGNUP_FILING = "Filing it… 📋";

/**
 * What the thinking bubble says while Kin works, advancing every ~2s and
 * holding on the last line. Nov 2 2026: one static line looked frozen.
 */
export const SIGNUP_READING_PHRASES = [
  "Reading your sheet…",
  "Finding the names…",
  "Looking for your row…",
  "Almost there…",
] as const;
export const SIGNUP_FILING_PHRASES = [
  "Filing it…",
  "Adding it to Tasks…",
  "Setting your reminder…",
] as const;

/** What Kin says when the photo is unreadable. Files nothing. */
export const SIGNUP_UNREADABLE = "I can't read this one. Try again with better light?";

/**
 * Which rows the "Which row is yours?" picker shows.
 *
 * Only rows somebody has SIGNED. A sign-up sheet is mostly blank lines
 * ("Other? List below" ×5, unsigned items), and none of those can be hers:
 * she is looking for the row she wrote her name on. Oct 27 2026: the first
 * real sheet produced 17 chips for 7 signed rows, and the Done button fell
 * off the bottom of the screen.
 *
 * Falls back to EVERY row when nothing on the sheet is signed, so a sheet
 * read before anyone wrote on it still lets her pick what she intends to
 * bring. Index values are preserved: the function files by `index`, and the
 * picker must hand back the same numbers the parser produced.
 */
export function signupPickerRows<T extends Pick<SignupRow, "person_text">>(rows: T[]): T[] {
  const signed = rows.filter((r) => (r?.person_text ?? "").trim().length > 0);
  return signed.length > 0 ? signed : rows;
}

/** The chip label for one row in the picker. */
export function signupRowChipLabel(row: Pick<SignupRow, "item" | "person_text">): string {
  const person = (row?.person_text ?? "").trim();
  const item = (row?.item ?? "").trim();
  return person ? `${item} · ${person}` : item;
}
